import React from 'react';
import type { Page, Component } from '../ComponentEditModal.types';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { ButtonsEditor } from '../shared/ButtonsEditor';
import type { ButtonData } from '../shared/ButtonsEditor';

interface SimpleHeaderData {
  title?: string;
  subtitle?: string;
  alignment?: 'left' | 'center' | 'right';
  size?: 'small' | 'medium' | 'large';
  customColors?: {
    background?: string;
    title?: string;
    subtitle?: string;
  };
  buttons?: ButtonData[];
}

interface SimpleHeaderEditorProps {
  formData: SimpleHeaderData;
  setFormData: (data: SimpleHeaderData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
}

export const SimpleHeaderEditor: React.FC<SimpleHeaderEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
}) => {
  const data = formData;
  const buttons = data.buttons || [];
  const themeColors = useCustomColors();

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('simple-header', themeColors);

  return (
    <div className="space-y-6">
      {/* Content Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Content</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.title || ''}
              onChange={e => setFormData({ ...data, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter page title"
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">
              {(data.title || '').length} / 200 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subtitle (Optional)
            </label>
            <textarea
              value={data.subtitle || ''}
              onChange={e => setFormData({ ...data, subtitle: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter optional subtitle"
              rows={2}
              maxLength={300}
            />
            <p className="text-xs text-gray-500 mt-1">
              {(data.subtitle || '').length} / 300 characters
            </p>
          </div>
        </div>
      </div>

      {/* Layout Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Layout</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Alignment</label>
            <select
              value={data.alignment || 'center'}
              onChange={e =>
                setFormData({ ...data, alignment: e.target.value as SimpleHeaderData['alignment'] })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
            <select
              value={data.size || 'medium'}
              onChange={e =>
                setFormData({ ...data, size: e.target.value as SimpleHeaderData['size'] })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Controls padding around the header</p>
          </div>
        </div>
      </div>

      {/* Call-to-Action Buttons */}
      <ButtonsEditor
        buttons={buttons}
        onChange={newButtons => setFormData({ ...data, buttons: newButtons })}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        label="Call-to-Action Buttons (Optional)"
      />

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize header colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
        onClearAllColors={() => setFormData({ ...data, customColors: undefined })}
      />
    </div>
  );
};
